// "YOUR BED" — the player's institutional hospital bed. Walking up and
// pressing E triggers the sleep transition that redirects to the
// Vibe Jam portal. Visually re-themed from the old woodcabin to fit
// the mental-institution lobby: steel frame, thin mattress, nightstand
// with a nameplate, IV stand. The bed-position + interact radius shape
// is unchanged so main.ts gating still works.

import * as THREE from 'three';

export type DreamersCabinHandle = {
  group: THREE.Group;
  bedPosition: THREE.Vector3;
  interactRadius: number;
  isNearBed: (x: number, z: number) => boolean;
  dispose: () => void;
};

const BED_INTERACT_RADIUS = 2.6;

function makeNameplate(text: string): THREE.Mesh {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#f3f4ec';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = '#4dd4c6';
  ctx.lineWidth = 8;
  ctx.strokeRect(12, 12, c.width - 24, c.height - 24);
  ctx.fillStyle = '#0f172a';
  ctx.font = '900 64px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, c.width / 2, c.height / 2 + 4);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return new THREE.Mesh(
    new THREE.PlaneGeometry(1.6, 0.4),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide }),
  );
}

export function buildDreamersCabin(
  parent: THREE.Object3D,
  origin: { x: number; z: number },
): DreamersCabinHandle {
  const group = new THREE.Group();
  group.position.set(origin.x, 0, origin.z);

  // Low rug — a softer vinyl patch under the bed so the cold linoleum
  // floor of the lobby doesn't swallow the fixture visually.
  const rug = new THREE.Mesh(
    new THREE.PlaneGeometry(5.4, 4.4),
    new THREE.MeshStandardMaterial({
      color: 0xc7d2fe, roughness: 0.9,
    }),
  );
  rug.rotation.x = -Math.PI / 2;
  rug.position.y = 0.05;
  group.add(rug);

  // Steel bed frame — low profile, white-painted metal with side rails.
  const steelMat = new THREE.MeshStandardMaterial({
    color: 0xe5e7eb, roughness: 0.35, metalness: 0.6,
  });
  const frame = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.25, 2.0), steelMat);
  frame.position.set(0, 0.55, -1.6);
  group.add(frame);
  // Posts at corners
  for (const [x, z] of [[-1.5, -2.55], [1.5, -2.55], [-1.5, -0.65], [1.5, -0.65]] as const) {
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 1.4, 8),
      steelMat,
    );
    post.position.set(x, 0.7, z);
    group.add(post);
  }
  // Head and foot rails (horizontal tubes)
  const railHead = new THREE.Mesh(
    new THREE.BoxGeometry(3.2, 0.12, 0.12),
    steelMat,
  );
  railHead.position.set(0, 1.35, -2.55);
  group.add(railHead);
  const railFoot = railHead.clone();
  railFoot.position.set(0, 1.05, -0.65);
  group.add(railFoot);

  // Thin mattress — institutional blue-striped vinyl look
  const mattress = new THREE.Mesh(
    new THREE.BoxGeometry(3.0, 0.25, 1.8),
    new THREE.MeshStandardMaterial({
      color: 0xf8fafc, emissive: 0x60a5fa, emissiveIntensity: 0.1, roughness: 0.6,
    }),
  );
  mattress.position.set(0, 0.82, -1.6);
  group.add(mattress);

  // Pillow
  const pillow = new THREE.Mesh(
    new THREE.BoxGeometry(1.0, 0.2, 1.2),
    new THREE.MeshStandardMaterial({ color: 0xe2e8f0 }),
  );
  pillow.position.set(-0.85, 1.04, -1.6);
  group.add(pillow);

  // Thin institutional blanket — muted teal
  const blanket = new THREE.Mesh(
    new THREE.BoxGeometry(2.6, 0.1, 1.6),
    new THREE.MeshStandardMaterial({
      color: 0x2dd4bf, emissive: 0x0f766e, emissiveIntensity: 0.2, roughness: 0.7,
    }),
  );
  blanket.position.set(0.3, 1.02, -1.6);
  group.add(blanket);

  // Nightstand to the right of the bed — a small cabinet with the
  // player's nameplate "YOUR BED".
  const standMat = new THREE.MeshStandardMaterial({
    color: 0xd1d5db, roughness: 0.5, metalness: 0.4,
  });
  const stand = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.1, 0.8), standMat);
  stand.position.set(2.3, 0.55, -1.6);
  group.add(stand);
  // Nameplate on the front of the nightstand
  const plate = makeNameplate('YOUR BED');
  plate.position.set(2.3, 1.25, -1.2);
  plate.rotation.y = 0;
  group.add(plate);

  // IV stand — tall pole + hanging bag
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x9ca3af, roughness: 0.25, metalness: 0.85,
  });
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 3.0, 8), poleMat);
  pole.position.set(-2.0, 1.5, -2.4);
  group.add(pole);
  const ivBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.35, 0.35, 0.08, 12),
    poleMat,
  );
  ivBase.position.set(-2.0, 0.04, -2.4);
  group.add(ivBase);
  const ivHook = new THREE.Mesh(
    new THREE.TorusGeometry(0.12, 0.02, 6, 12),
    poleMat,
  );
  ivHook.rotation.x = Math.PI / 2;
  ivHook.position.set(-2.0, 3.0, -2.4);
  group.add(ivHook);
  const ivBag = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.5, 0.08),
    new THREE.MeshStandardMaterial({
      color: 0xbae6fd, emissive: 0x38bdf8, emissiveIntensity: 0.35,
      transparent: true, opacity: 0.75,
    }),
  );
  ivBag.position.set(-2.0, 2.75, -2.4);
  group.add(ivBag);

  // Soft sconce glow above the headboard — replaces the old lantern.
  const lantern = new THREE.PointLight(0x93c5fd, 2.4, 10, 2);
  lantern.position.set(0, 2.6, -2.6);
  group.add(lantern);
  const lanternOrb = new THREE.Mesh(
    new THREE.SphereGeometry(0.2, 10, 8),
    new THREE.MeshStandardMaterial({
      color: 0xbfdbfe, emissive: 0x93c5fd, emissiveIntensity: 1.0,
      transparent: true, opacity: 0.9,
    }),
  );
  lanternOrb.position.set(0, 2.6, -2.6);
  group.add(lanternOrb);

  parent.add(group);

  const bedWorld = new THREE.Vector3(origin.x, 1.35, origin.z - 1.6);

  return {
    group,
    bedPosition: bedWorld,
    interactRadius: BED_INTERACT_RADIUS,
    isNearBed(x: number, z: number) {
      const dx = x - bedWorld.x;
      const dz = z - bedWorld.z;
      return dx * dx + dz * dz <= BED_INTERACT_RADIUS * BED_INTERACT_RADIUS;
    },
    dispose() {
      parent.remove(group);
    },
  };
}

import { fadeMusicOut } from '../audio/synth';

const FADE_MS = 3000;        // black overlay → fully opaque
const MUSIC_FADE_MS = 2500;  // music → silence (lands before the screen goes black)

// Full-screen sleeping overlay. Two stacked layers:
//
//   1. Blue dream layer — radial-gradient background + floating ZZZ
//      + title text. Appears quickly (600 ms) so the "drifting off"
//      beat reads immediately.
//   2. Black fade layer — sits on top, starts fully transparent and
//      ramps to solid black over FADE_MS. The redirect fires when
//      it's fully opaque so the destination page loads on a black
//      frame with no visible pop.
//
// Music ducks to silence over MUSIC_FADE_MS in parallel.
export function playSleepTransition(): Promise<void> {
  return new Promise((resolve) => {
    fadeMusicOut(MUSIC_FADE_MS);

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: radial-gradient(circle at center, rgba(30,41,59,0.85), rgba(2,6,23,0.98));
      z-index: 9999;
      opacity: 0;
      transition: opacity 600ms ease;
      pointer-events: all;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 24px;
      color: #e5e7eb;
      text-align: center;
      font: 700 26px system-ui, sans-serif;
      text-shadow: 0 0 28px #60a5fa;
    `;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes dreamers-zfloat {
        0%   { transform: translateY(0)    rotate(0deg);  opacity: 0.2; }
        35%  { transform: translateY(-16px) rotate(-8deg); opacity: 1;   }
        100% { transform: translateY(-40px) rotate(8deg);  opacity: 0;   }
      }
      @keyframes dreamers-titlepulse {
        0%, 100% { opacity: .75; letter-spacing: 2px; }
        50%      { opacity: 1;   letter-spacing: 5px; }
      }
      @keyframes dreamers-fadetoblack {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `;
    overlay.appendChild(style);

    const z = document.createElement('div');
    z.style.cssText = `
      font: 900 52px system-ui, sans-serif;
      color: #93c5fd;
      letter-spacing: 16px;
      animation: dreamers-zfloat 1.4s ease-out infinite;
    `;
    z.textContent = 'Z z z';
    overlay.appendChild(z);

    const title = document.createElement('div');
    title.style.cssText = `animation: dreamers-titlepulse 1.6s ease-in-out infinite;`;
    title.textContent = 'Drifting off to another world…';
    overlay.appendChild(title);

    const sub = document.createElement('div');
    sub.style.cssText = `font: 500 13px system-ui; opacity:.65; letter-spacing:1.5px;`;
    sub.textContent = 'VIBE JAM PORTAL';
    overlay.appendChild(sub);

    // Black fade layer on top of the blue dream layer. Uses a CSS
    // keyframe animation (not a transition) because transitions on a
    // freshly appended element can skip straight to the end if the
    // browser hasn't painted the initial opacity:0 yet. Keyframes
    // always run from 0% → 100% on insert.
    const fadeBlack = document.createElement('div');
    fadeBlack.style.cssText = `
      position: absolute;
      inset: 0;
      background: #000;
      opacity: 0;
      animation: dreamers-fadetoblack ${FADE_MS}ms ease-in forwards;
      pointer-events: none;
    `;
    overlay.appendChild(fadeBlack);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });
    setTimeout(() => resolve(), FADE_MS);
  });
}
